import * as React from 'react';
import { useContext, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { ViewerContext } from '../../providers/ViewerContextProvider';
import { LoadingContainer, PageContainer } from '../../components/Layout';
import { useBreadcrumbs } from '@/components/Breadcrumbs/useBreadcrumbs';
import { MerchantGeneralTab } from './MerchantGeneralTab';
import { MerchantOpeningHoursTab } from './MerchantOpeningHoursTab';
import { MerchantMenuTab } from './MerchantMenuTab';
import { MerchantOrdersSettingsTab } from './MerchantOrdersSettingsTab';
import { StorePayment } from '../StorePayment/StorePayment';
import { useQuery } from '@apollo/client';
import { adminStoreQuery } from '../../graphql/queries';
import WebsiteWidgetTab from './WebsiteWidgetTab';
import { MerchantOrdersTab } from './MerchantOrdersTab';
import MerchantTransactionsTab from './MerchantTransactionsTab';
import i18n from '@/utils/i18n';
import { OfflineCardTab } from './OfflineCardTab';
import { MerchantInvoiceTab } from './MerchantInvoiceTab';
import { StockTab } from './StockTab';
import { MerchantOrderOverviewTab } from './MerchantOrderOverviewTab';
import { PartnerIntegrationsTab } from './PartnerIntegrationsTab';
import { SelfReturnQrCodeTab } from './SelfReturnQrCodeTab';
import {
  OnboardingStatus,
  ServicePackageType,
  StoreType,
} from '../../graphql/generatedTypes';
import CustomerCateringUserListTab from './CustomerCateringUserListTab';
import InventoryCheckTab from './InventoryCheckTab/InventoryCheckTab';
import { SustainabilityReportsTab } from './SustainabilityReportsTab';
import { MerchantManualsTab } from './MerchantManualsTab';
import { MerchantCurrentAssets } from './Assets/MerchantCurrentAssets';
import { Routes } from '@/utils/routes';
import {
  ContextSidebarConfig,
  useRouteSidebar,
} from '../../components/SidebarContext';
import type { IconName } from '@/utils/iconName';

type StoreTabGroup =
  | 'profile'
  | 'orders'
  | 'payments'
  | 'containers'
  | 'more'
  | 'help';

type StoreTab = {
  title: string;
  content: React.ReactNode;
  iconName: IconName;
  group: StoreTabGroup;
};

// Group order + labels for the sidebar (labels reuse the existing Tabs:* keys).
const SIDEBAR_GROUPS: { id: StoreTabGroup; label: string }[] = [
  { id: 'profile', label: i18n.t('Tabs:profile') },
  { id: 'orders', label: i18n.t('Tabs:orders') },
  { id: 'payments', label: i18n.t('Tabs:payments') },
  { id: 'containers', label: i18n.t('Tabs:containersAndStock') },
  { id: 'more', label: i18n.t('Tabs:more') },
  { id: 'help', label: i18n.t('Tabs:help') },
];

function PartnerMerchantStorePage() {
  const viewer = useContext(ViewerContext);
  // Empty string acts as "no store"; the guard below returns before any tab
  // content (which requires a real id) is rendered.
  const storeId = viewer.storeId ?? '';
  const history = useHistory();
  const location = useLocation();

  const { data, loading } = useQuery(adminStoreQuery, {
    variables: { id: storeId },
  });

  const vytalStoreId = data?.store?.external_vytal_id;

  // The store name lives in the breadcrumb (like Ops Orders' page breadcrumb);
  // each tab renders its own title, so there is no PageHeader here.
  const storeTitle = data?.store
    ? `${data.store.brand.name} - ${data.store.name}`
    : '';

  useBreadcrumbs({
    id: 'partner-merchant-store',
    priority: 100,
    enabled: storeTitle !== '',
    items: [
      {
        label: storeTitle,
        href: Routes.partnerMerchants(),
      },
    ],
  });

  // Mirror the sustainability report eligibility from colugo (getEligibleStores):
  // eligible service package, OR any active store in Spain/US regardless of package.
  // A store can opt out of sustainability reports (sustainability_reports_enabled);
  // the partner then does not get the tab at all.
  const isEligibleForSustainabilityReport =
    data?.store?.sustainability_reports_enabled !== false &&
    ([
      ServicePackageType.Essential,
      ServicePackageType.ProPlus,
      ServicePackageType.Enterprise,
    ].includes(data?.store?.service_package_type) ||
      (['ES', 'US'].includes(data?.store?.country_code) &&
        data?.store?.onboarding_status === OnboardingStatus.Active));

  const tabs: StoreTab[] = [
    {
      title: i18n.t('Tabs:generalInfo'),
      iconName: 'info-circle',
      group: 'profile',
      content: (
        <MerchantGeneralTab
          externalPickpackId={storeId}
          title={i18n.t('Tabs:generalInfo')}
        />
      ),
    },
    {
      title: i18n.t('Common:openingHours'),
      iconName: 'clock',
      group: 'profile',
      content: (
        <MerchantOpeningHoursTab
          externalPickpackId={storeId}
          title={i18n.t('Common:openingHours')}
        />
      ),
    },
    {
      title: i18n.t('Tabs:menu'),
      iconName: 'knife-fork-food-menu',
      group: 'profile',
      content: (
        <MerchantMenuTab
          externalPickpackId={storeId}
          title={i18n.t('Tabs:menu')}
        />
      ),
    },
    {
      title: i18n.t('Common:onlineOrders'),
      iconName: 'shopping-cart-webshop',
      group: 'orders',
      content: (
        <MerchantOrdersTab
          externalPickpackId={storeId}
          title={i18n.t('Common:onlineOrders')}
        />
      ),
    },
    {
      title: i18n.t('Tabs:ordersSetting'),
      iconName: 'settings-gear',
      group: 'orders',
      content: (
        <MerchantOrdersSettingsTab
          externalPickpackId={storeId}
          vytalStoreId={vytalStoreId}
          title={i18n.t('Tabs:ordersSetting')}
        />
      ),
    },
    {
      title: i18n.t('Common:banner'),
      iconName: 'world-globe-website',
      group: 'orders',
      content: (
        <WebsiteWidgetTab
          merchantStoreId={vytalStoreId}
          title={i18n.t('Common:banner')}
        />
      ),
    },
    {
      title: i18n.t('Common:paymentMehtods'),
      iconName: 'card-credit',
      group: 'payments',
      content: (
        <StorePayment
          storeId={storeId}
          title={i18n.t('Common:paymentMehtods')}
        />
      ),
    },
    {
      title: i18n.t('Common:transactions'),
      iconName: 'currency-money-euro',
      group: 'payments',
      content: (
        <MerchantTransactionsTab
          vytalStoreGuid={vytalStoreId}
          title={i18n.t('Common:transactions')}
        />
      ),
    },
    {
      title: i18n.t('Common:invoices'),
      iconName: 'invoice-receipt-bill',
      group: 'payments',
      content: (
        <MerchantInvoiceTab
          externalPickpackId={storeId}
          title={i18n.t('Common:invoices')}
        />
      ),
    },
    {
      title: i18n.t('Common:offlineCards'),
      iconName: 'card-vytal-offline',
      group: 'containers',
      content: (
        <OfflineCardTab
          vytalStoreGuid={vytalStoreId}
          title={i18n.t('Common:offlineCards')}
        />
      ),
    },
    {
      title: i18n.t('Common:inventoryCheck'),
      iconName: 'clipboard-container-check',
      group: 'containers',
      content: (
        <InventoryCheckTab
          vytalStoreGuid={vytalStoreId}
          title={i18n.t('Common:inventoryCheck')}
        />
      ),
    },
    {
      title: i18n.t('Common:stock'),
      iconName: 'container-bowl-cup-stock',
      group: 'containers',
      content: (
        <StockTab
          vytalStoreId={vytalStoreId}
          pickpackStoreId={storeId}
          title={i18n.t('Common:stock')}
        />
      ),
    },
    {
      title: 'Assets',
      iconName: 'swiss-knife-asset',
      group: 'containers',
      content: <MerchantCurrentAssets storeId={storeId} />,
    },
    {
      title: i18n.t('Common:containerOrders'),
      iconName: 'box',
      group: 'containers',
      content: (
        <MerchantOrderOverviewTab
          vytalStoreId={vytalStoreId}
          title={i18n.t('Common:containerOrders')}
        />
      ),
    },
    {
      title: i18n.t('Tabs:integrations'),
      iconName: 'link',
      group: 'more',
      content: (
        <PartnerIntegrationsTab
          vytalStoreId={vytalStoreId}
          title={i18n.t('Tabs:integrations')}
        />
      ),
    },
    ...(data?.store?.type === StoreType.Caterer
      ? [
          {
            title: i18n.t('Common:customerCateringUsers'),
            iconName: 'persons-group' as const,
            group: 'more' as const,
            content: (
              <CustomerCateringUserListTab
                storeTextId={storeId}
                title={i18n.t('Common:customerCateringUsers')}
              />
            ),
          },
        ]
      : []),
    {
      title: i18n.t('SelfReturnQrCodesTab:selfReturnQrCodes'),
      iconName: 'sticker-qr-return',
      group: 'more',
      content: (
        <SelfReturnQrCodeTab
          storeUuid={vytalStoreId}
          storeTextId={storeId}
          title={i18n.t('SelfReturnQrCodesTab:selfReturnQrCodes')}
        />
      ),
    },
    // Sustainability Reports tab - mirrors colugo getEligibleStores: eligible service
    // package, OR any active store in Spain/US regardless of package.
    ...(isEligibleForSustainabilityReport
      ? [
          {
            title: i18n.t('SustainabilityReports:tabTitle'),
            iconName: 'leaf' as const,
            group: 'more' as const,
            content: (
              <SustainabilityReportsTab
                vytalStoreId={vytalStoreId}
                title={i18n.t('SustainabilityReports:tabTitle')}
                isEligible={isEligibleForSustainabilityReport}
              />
            ),
          },
        ]
      : []),
    // Appended last on purpose: sidebar links address tabs by array index, so
    // inserting earlier would repoint every existing ?activeTab=NN link.
    {
      title: i18n.t('Tabs:help'),
      iconName: 'documents',
      group: 'help',
      content: <MerchantManualsTab title={i18n.t('Tabs:help')} />,
    },
  ];

  // Build the context sidebar from the tab list so link indices stay aligned
  // with the rendered content. Active tab is tracked via the ?activeTab query
  // param (zero-padded so e.g. tab 1 doesn't also match tab 13).
  const sidebarConfig: ContextSidebarConfig = {
    mode: 'grouped',
    groups: SIDEBAR_GROUPS.map(({ id, label }) => ({
      label,
      isActiveMatchingMethod: 'searchParam' as const,
      links: tabs
        .map((tab, index) => ({ tab, index }))
        .filter(({ tab }) => tab.group === id)
        .map(({ tab, index }) => ({
          label: tab.title,
          href: `${Routes.partnerMerchants()}?activeTab=${String(
            index,
          ).padStart(2, '0')}`,
          iconName: tab.iconName,
        })),
    })).filter((group) => group.links.length > 0),
  };

  useRouteSidebar(storeId ? sidebarConfig : null);

  // Default to the first tab so the sidebar selection and content stay in sync.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (storeId && !params.has('activeTab')) {
      history.replace(`${Routes.partnerMerchants()}?activeTab=00`);
    }
  }, [storeId, location.search, history]);

  if (!storeId) {
    return <p>{i18n.t('Merchants:noStoreIdAvailable')}</p>;
  }

  if (data == null) {
    return <p>{i18n.t('Common:loading')}</p>;
  }

  const activeParam = new URLSearchParams(location.search).get('activeTab');
  const parsedIndex = Number(activeParam);
  const activeIndex =
    Number.isInteger(parsedIndex) &&
    parsedIndex >= 0 &&
    parsedIndex < tabs.length
      ? parsedIndex
      : 0;

  return (
    <PageContainer>
      {loading && <LoadingContainer />}
      {tabs[activeIndex]?.content}
    </PageContainer>
  );
}

export default PartnerMerchantStorePage;
